import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import Logo from "./Logo";
import TravelNavBadge from "./TravelNavBadge";
import LanguageSwitcher from "./LanguageSwitcher";
import { useT } from "../hooks/useT";
import { Phone, Menu, X, User } from "lucide-react";

const Header = ({ onCheckCoverage }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const t = useT();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const navItems = [
    { to: "/fibra-movil", label: t("nav_fibra_movil") },
    { to: "/tarifas-movil", label: t("nav_tarifas_movil") },
    { to: "/fibra-fijo", label: t("nav_fibra_fijo") },
    { to: "/empresas", label: t("nav_empresas") },
    { to: "/empresas#eventos", label: t("nav_eventos") },
    { to: "/tv", label: t("nav_tv") },
    { to: "/roaming", label: t("nav_roaming") },
    ...(process.env.REACT_APP_ENABLE_TRAVEL === "true" &&
       process.env.REACT_APP_TRAVEL_VISIBLE_IN_NAV === "true"
       ? [{ to: "/travel", label: t("nav_travel") }] : []),
    { to: "/internet-rural", label: t("nav_internet_rural") },
  ];

  const isActive = (path) => location.pathname === path;
  const hideCoverageCTA = location.pathname === "/tarifas-movil";
  const isPeninsula = location.pathname === "/peninsula";
  const visibleNavItems = isPeninsula ? navItems.filter(i => i.to !== "/internet-rural") : navItems;
  // On /peninsula these tariff sections live in-page (anchors), not on the Canarias-only routes.
  const PENINSULA_ANCHORS = { "/fibra-movil": "fibra-movil", "/tarifas-movil": "tarifas-movil", "/fibra-fijo": "fibra-fijo", "/empresas": "empresas", "/tv": "livewifi-tv" };

  return (
    <header
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled || location.pathname !== "/" ? "bg-[#0a0a0a]/95 backdrop-blur-lg py-2 shadow-lg" : "bg-transparent py-4"
      }`}
    >
      {/* Top Bar */}
      <div className="hidden lg:block border-b border-white/10 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex items-center justify-between text-sm">
          <div className="flex items-center gap-6">
            <Link to="/" className="text-white font-medium hover:text-[#7DD500] transition-colors">{t("nav_particulares")}</Link>
            {isPeninsula ? (
              <a href="#empresas" className="text-white/60 hover:text-[#7DD500] transition-colors">{t("nav_empresas_top")}</a>
            ) : (
              <Link to="/empresas" className="text-white/60 hover:text-[#7DD500] transition-colors">{t("nav_empresas_top")}</Link>
            )}
            {!isPeninsula && <Link to="/estudio-zona" className="text-[#7DD500] font-bold hover:text-[#95E619] transition-colors">{t("nav_estudio_zona")}</Link>}
            <Link to="/test-velocidad" className="text-white/60 hover:text-[#7DD500] transition-colors">{t("nav_test_velocidad")}</Link>
          </div>
          <div className="flex items-center gap-5">
            <LanguageSwitcher />
            <span className="text-white/60">{t("nav_call_us")}</span>
            <a href="tel:+34828182100" className="text-[#7DD500] font-bold flex items-center gap-1">
              <Phone className="w-4 h-4" />
              828 182 100
            </a>
            <a
              href="https://clienteslivewifi.ispgestion.com/site/login"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-1.5 bg-[#7DD500]/10 border border-[#7DD500]/30 rounded-full text-[#7DD500] hover:bg-[#7DD500]/20 transition-all"
            >
              <User className="w-4 h-4" />
              {t("nav_clients_area")}
            </a>
          </div>
        </div>
      </div>

      {/* Main Nav */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between py-2">
          <Link to="/" data-testid="logo" className="flex items-center">
            <Logo className="h-10" />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {visibleNavItems.map((item) => (
              isPeninsula && PENINSULA_ANCHORS[item.to] ? (
                <a
                  key={item.to}
                  href={`#${PENINSULA_ANCHORS[item.to]}`}
                  className="px-3 py-2 font-medium transition-colors text-sm text-white/90 hover:text-[#7DD500]"
                >
                  {item.label}
                </a>
              ) : (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`px-3 py-2 font-medium transition-colors text-sm ${
                    isActive(item.to) ? "text-[#7DD500]" : "text-white/90 hover:text-[#7DD500]"
                  }`}
                >
                  {item.to === "/travel" ? <TravelNavBadge active={isActive(item.to)} /> : item.label}
                </Link>
              )
            ))}
          </nav>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center gap-3">
            {!hideCoverageCTA && (
              <button
                data-testid="header-check-coverage-btn"
                onClick={onCheckCoverage}
                className="px-5 py-2.5 bg-[#7DD500] text-black font-bold rounded-lg hover:bg-[#95E619] transition-all text-sm"
              >
                {t("nav_check_coverage")}
              </button>
            )}
          </div>

          {/* Mobile */}
          <div className="lg:hidden flex items-center gap-2">
            <LanguageSwitcher variant="compact" />
            <a
              href="https://clienteslivewifi.ispgestion.com/site/login"
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 text-[#7DD500]"
              aria-label={t("nav_clients_area")}
            >
              <User className="w-5 h-5" />
            </a>
            <button
              data-testid="mobile-menu-btn"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 text-white"
              aria-label="Menu"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <nav className="lg:hidden mt-2 glass rounded-xl p-4 flex flex-col gap-2">
            {visibleNavItems.map((item) => (
              isPeninsula && PENINSULA_ANCHORS[item.to] ? (
                <a
                  key={item.to}
                  href={`#${PENINSULA_ANCHORS[item.to]}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-left py-2 font-medium text-white/80 hover:text-[#7DD500]"
                >
                  {item.label}
                </a>
              ) : (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`text-left py-2 font-medium ${isActive(item.to) ? "text-[#7DD500]" : "text-white/80 hover:text-[#7DD500]"}`}
                >
                  {item.to === "/travel" ? <TravelNavBadge active={isActive(item.to)} /> : item.label}
                </Link>
              )
            ))}
            <hr className="border-white/10 my-2" />
            {!isPeninsula && <Link
              to="/estudio-zona"
              data-testid="mobile-link-estudio-zona"
              className={`text-left py-2 font-bold ${isActive("/estudio-zona") ? "text-[#95E619]" : "text-[#7DD500] hover:text-[#95E619]"}`}
            >
              {t("nav_estudio_zona")}
            </Link>}
            <Link
              to="/test-velocidad"
              data-testid="mobile-link-test-velocidad"
              className={`text-left py-2 font-medium ${isActive("/test-velocidad") ? "text-[#7DD500]" : "text-white/80 hover:text-[#7DD500]"}`}
            >
              {t("nav_test_velocidad")}
            </Link>
            <hr className="border-white/10 my-2" />
            <a
              href="https://clienteslivewifi.ispgestion.com/site/login"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-[#7DD500] py-2"
            >
              <User className="w-5 h-5" />
              {t("nav_clients_area")}
            </a>
            <a href="tel:+34828182100" className="flex items-center gap-2 text-white py-2">
              <Phone className="w-5 h-5 text-[#7DD500]" />
              828 182 100
            </a>
            <button onClick={onCheckCoverage} className={`mt-2 px-6 py-3 bg-[#7DD500] text-black font-bold rounded-lg text-center ${hideCoverageCTA ? "hidden" : ""}`}>
              {t("nav_check_coverage_short")}
            </button>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
